import React from "react";
import { ReactComponent as InfoIcon } from "../../../../../Assets/SettingsPages/InfoIcon.svg";
import Toggle from "../../../../../Components/Atoms/Toggle";
import {
  AdyenPaymentMethodType,
  AdyenPaymentMethodVerificationStatus
} from "../../../../../Contracts/Transaction";
import { AdyenPartnerConfiguredPaymentMethodView } from "../../../../../Contracts/Settings";
import { PaymentMethodDisplayAttributes } from "../../../../../Contracts/Finance";
import { useTeamStore } from "../../../../../Store/Team";

export interface ConfigurePaymentMethodCardProps {
  method: AdyenPartnerConfiguredPaymentMethodView;
  onChange: (method: AdyenPartnerConfiguredPaymentMethodView) => void;
}

const ConfigurePaymentMethodCard: React.FC<ConfigurePaymentMethodCardProps> = ({
  method,
  onChange
}) => {
  const displayAttributes = new PaymentMethodDisplayAttributes(method);
  const { currentTeam } = useTeamStore();

  const handleFeeTitleChange = (value: string) => {
    onChange({
      ...method,
      surcharges_fee_description: value
    });
  };

  return (
    <div className="bg-white border border-gray-200 my-4">
      <div className="p-4 flex items-center space-x-2 bg-[#f0f5ff]">
        <input
          type="checkbox"
          checked={method.active}
          className="h-4 w-4 mr-2"
          onChange={e =>
            onChange({
              ...method,
              active: e.target.checked
            })
          }
        />
        <img src={displayAttributes.logoSrc} alt={displayAttributes.displayName} />
        <label>{displayAttributes.displayName}</label>
        {method.verification_status !== AdyenPaymentMethodVerificationStatus.Valid && (
          <div className="capitalize">({method.verification_status})</div>
        )}
      </div>
      {method.active && (
        <>
          {method.type === AdyenPaymentMethodType.Ach && (
            <div className="bg-orange-100 mx-8 my-4 text-gray-700">
              <div className="flex items-start p-4">
                <InfoIcon className="w-12 text-orange-600 mt-1" />
                <p className="ml-2">
                  To prevent unnecessary chargebacks, ensure your business customers instruct their
                  bank to add Adyen's ACH Company ID 8263863381 to the list of entities that are
                  allowed to debit or credit their business account. If a chargeback occurs as a
                  result of this cause, you will incur charges for it.{" "}
                  <a
                    href="https://help.zomentum.com/support/solutions/articles/44002477981-direct-debit-ach-usa-"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    Learn More
                  </a>
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col mx-8 my-4 space-x-2">
            <div className="flex items-center my-2">
              <Toggle
                checked={method.is_surcharges_fee_enable}
                onChange={enabled =>
                  onChange({
                    ...method,
                    is_surcharges_fee_enable: enabled,
                    surcharges_fee_description:
                      enabled && !method.surcharges_fee_description?.trim()
                        ? "Transaction Fee"
                        : method.surcharges_fee_description
                  })
                }
                className="scale-75"
              />
              <label>Transaction Fee</label>
            </div>
            {method.is_surcharges_fee_enable && (
              <div className="flex my-2 flex-wrap">
                <div className="flex flex-col space-y-2 mr-12 flex-grow max-w-72">
                  <label>Transaction Fee Title</label>
                  <input
                    className={`rounded px-3 py-1 border ${
                      !method.surcharges_fee_description?.trim()
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    value={method.surcharges_fee_description}
                    onChange={e => handleFeeTitleChange(e.target.value)}
                    maxLength={50}
                  />
                  {!method.surcharges_fee_description?.trim() && (
                    <span className="text-red-500 text-sm">Title cannot be empty.</span>
                  )}
                </div>
                <div className="flex flex-col space-y-2">
                  <label>Value</label>
                  <div className="flex space-x-2 items-center">
                    <div className="flex">
                      <input
                        className="rounded-l w-20 px-3 py-1 border-y border-l border-gray-300"
                        value={method.surcharges_fee_percentage || 0}
                        onChange={e =>
                          onChange({
                            ...method,
                            surcharges_fee_percentage: parseFloat(e.target.value)
                          })
                        }
                        type="number"
                        step="any"
                        min={0}
                      />
                      <div className="rounded-r px-2 py-1 border border-gray-300 bg-gray-100 text-gray-600 mr-2">
                        %
                      </div>
                    </div>
                    +
                    <div className="flex">
                      <input
                        className="rounded-l w-20 px-3 py-1 border-y border-l border-gray-300"
                        value={method.surcharges_fee_fixed || 0}
                        onChange={e =>
                          onChange({
                            ...method,
                            surcharges_fee_fixed: parseFloat(e.target.value)
                          })
                        }
                        type="number"
                        step="any"
                        min={0}
                      />
                      <div className="rounded-r px-2 py-1 border border-gray-300 bg-gray-100 text-gray-600 mr-2">
                        {currentTeam?.currency}
                      </div>
                    </div>
                  </div>
                  {(method.surcharges_fee_percentage || 0) === 0 &&
                    (method.surcharges_fee_fixed || 0) === 0 && (
                      <span className="text-red-500 text-sm text-wrap w-full">
                        Fee must be greater than 0.
                      </span>
                    )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ConfigurePaymentMethodCard;
